/**
 * When the cart has only secret-offer line items (no normal products), those lines
 * are removed automatically. Checkout is disabled in that state; clearing on
 * checkout click is a fallback if disable did not apply.
 */

import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SOURCE = 'cart-secret-offer-checkout';
const BLOCK_ATTR = 'data-secret-offer-only-blocked';

class CartSecretOfferCheckout {
  #isClearing = false;

  constructor() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#setup());
    } else {
      this.#setup();
    }
  }

  #setup() {
    this.#attachListeners();
    this.#syncCheckoutState();
    this.#observeCartDom();

    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      if (event.detail?.data?.source === SOURCE) return;
      requestAnimationFrame(() => this.#syncCheckoutState());
      window.setTimeout(() => this.#syncCheckoutState(), 120);
      window.setTimeout(() => this.#syncCheckoutState(), 500);
    });

    window.setTimeout(() => this.#syncCheckoutState(), 600);
  }

  #observeCartDom() {
    const targets = [
      document.querySelector('.cart-drawer__items'),
      document.querySelector('.cart-page__items'),
      document.querySelector('.cart-items__wrapper'),
    ].filter(Boolean);

    if (!targets.length) {
      window.setTimeout(() => this.#observeCartDom(), 200);
      return;
    }

    const observer = new MutationObserver(() => {
      this.#syncCheckoutState();
    });

    for (const target of targets) {
      observer.observe(target, { childList: true, subtree: true });
    }
  }

  /**
   * @param {object} item
   * @returns {boolean}
   */
  #isSecretOfferLineItem(item) {
    const properties = item?.properties;
    if (!properties) return false;

    if (Array.isArray(properties)) {
      return properties.some(
        (prop) => prop?.first === '_secret_offer' && String(prop?.last).toLowerCase() === 'true'
      );
    }

    return String(properties._secret_offer).toLowerCase() === 'true';
  }

  /**
   * @param {object} cart
   * @returns {boolean}
   */
  #isSecretOfferOnlyCart(cart) {
    if (!cart?.items?.length) return false;
    return cart.items.every((item) => this.#isSecretOfferLineItem(item));
  }

  #isSecretOfferOnlyFromDom() {
    const hosts = document.querySelectorAll('cart-items-component[data-cart-secret-offer-only]');
    for (const host of hosts) {
      if (host.getAttribute('data-cart-secret-offer-only') === 'true') return true;
    }

    const rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    if (!rows.length) return false;

    const secretRows = document.querySelectorAll('[data-secret-offer-item="true"]');
    return secretRows.length > 0 && secretRows.length === rows.length;
  }

  /**
   * @returns {boolean}
   */
  shouldBlockCheckout() {
    return this.#isSecretOfferOnlyFromDom();
  }

  #getCheckoutButtons() {
    return /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll(
        '#checkout, button[name="checkout"], .cart__checkout-button, .sr-headless-checkout'
      ),
    ]);
  }

  /**
   * @param {boolean} block
   */
  #applyCheckoutBlock(block) {
    for (const button of this.#getCheckoutButtons()) {
      if (block) {
        button.setAttribute(BLOCK_ATTR, 'true');
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('secret-offer-checkout-blocked');
        if (button instanceof HTMLButtonElement) {
          button.disabled = true;
        } else {
          button.style.pointerEvents = 'none';
        }
      } else if (button.getAttribute(BLOCK_ATTR) === 'true') {
        button.removeAttribute(BLOCK_ATTR);
        button.removeAttribute('aria-disabled');
        button.classList.remove('secret-offer-checkout-blocked');
        button.style.pointerEvents = '';
        if (button instanceof HTMLButtonElement) {
          button.disabled = false;
        }
        button.removeAttribute('disabled');
      }
    }

    for (const wrapper of document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons')) {
      if (!(wrapper instanceof HTMLElement)) continue;
      wrapper.style.pointerEvents = block ? 'none' : '';
      wrapper.style.opacity = block ? '0.45' : '';
      wrapper.setAttribute('aria-hidden', block ? 'true' : 'false');
    }

    const cartForm = document.getElementById('cart-form');
    if (cartForm instanceof HTMLFormElement) {
      if (block) {
        cartForm.dataset.secretOfferOnlyCart = 'true';
      } else {
        delete cartForm.dataset.secretOfferOnlyCart;
      }
    }
  }

  /**
   * @param {object} cart
   */
  #setSecretOfferOnlyDataset(cart) {
    const block = this.#isSecretOfferOnlyCart(cart);
    for (const host of document.querySelectorAll('cart-items-component')) {
      host.setAttribute('data-cart-secret-offer-only', block ? 'true' : 'false');
    }
    return block;
  }

  async #syncCheckoutState() {
    if (this.#isClearing) return false;

    let cart = null;

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      cart = await response.json();
    } catch {
      const blockFromDom = this.#isSecretOfferOnlyFromDom();
      this.#applyCheckoutBlock(blockFromDom);
      return blockFromDom;
    }

    const block = this.#setSecretOfferOnlyDataset(cart);

    if (block) {
      await this.#clearCart();
      return false;
    }

    this.#applyCheckoutBlock(false);
    return false;
  }

  clearCartWhenBlocked() {
    if (!this.shouldBlockCheckout() && !this.#isSecretOfferOnlyFromDom()) return;
    return this.#clearCart();
  }

  async #clearCart() {
    if (this.#isClearing) return;
    this.#isClearing = true;

    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`Cart clear failed (${response.status})`);

      const cart = await response.json();
      document.dispatchEvent(
        new CartUpdateEvent(cart, SOURCE, {
          source: SOURCE,
          itemCount: cart.item_count,
        })
      );

      this.#setSecretOfferOnlyDataset(cart);
      this.#applyCheckoutBlock(false);
    } catch (error) {
      console.warn('[Secret offer checkout] Failed to clear secret-offer-only cart', error);
    } finally {
      this.#isClearing = false;
    }
  }

  /**
   * Fallback when block state is active but checkout was not actually disabled.
   * @param {HTMLElement|null} checkoutControl
   * @returns {boolean}
   */
  #shouldFallbackClearCart(checkoutControl) {
    if (!this.shouldBlockCheckout()) return false;
    if (!(checkoutControl instanceof HTMLElement)) return true;
    if (checkoutControl instanceof HTMLButtonElement && checkoutControl.disabled) return false;
    if (checkoutControl.getAttribute(BLOCK_ATTR) === 'true' && checkoutControl.style.pointerEvents === 'none') {
      return false;
    }
    return true;
  }

  /**
   * @param {Event} event
   * @param {HTMLElement|null} [checkoutControl]
   */
  #handleBlockedCheckoutFallback(event, checkoutControl = null) {
    const control =
      checkoutControl ||
      event.target.closest(
        '#checkout, button[name="checkout"], .cart__checkout-button, .sr-headless-checkout'
      );

    if (!this.#shouldFallbackClearCart(control)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.#clearCart();
  }

  #attachListeners() {
    document.addEventListener(
      'click',
      (event) => {
        const checkoutButton = event.target.closest(
          '#checkout, button[name="checkout"], .cart__checkout-button, .sr-headless-checkout'
        );
        if (!checkoutButton) return;
        this.#handleBlockedCheckoutFallback(event, checkoutButton);
      },
      true
    );

    const cartForm = document.getElementById('cart-form');
    cartForm?.addEventListener(
      'submit',
      (event) => {
        const submitter = event.submitter;
        const isCheckout =
          submitter?.getAttribute('name') === 'checkout' ||
          submitter?.id === 'checkout' ||
          submitter?.classList.contains('cart__checkout-button');

        if (!isCheckout && cartForm.dataset.secretOfferOnlyCart !== 'true') return;
        if (!this.shouldBlockCheckout() && cartForm.dataset.secretOfferOnlyCart !== 'true') return;

        this.#handleBlockedCheckoutFallback(event, submitter instanceof HTMLElement ? submitter : null);
      },
      true
    );
  }
}

const cartSecretOfferCheckout = new CartSecretOfferCheckout();

window.__cartSecretOfferOnlyBlocksCheckout = () => cartSecretOfferCheckout.shouldBlockCheckout();
window.__clearCartSecretOfferOnly = () => cartSecretOfferCheckout.clearCartWhenBlocked();

export default cartSecretOfferCheckout;
